import React from 'react';
import {
    Box,
    Container,
    Grid,
    Typography,
    Button,
    Chip,
    Stack,
    alpha,
} from '@mui/material';
import {
    ArrowForward,
    CheckCircle,
    Security,
    Speed,
    Assessment,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import VideoMeetingSteps from '../components/VideoMeetingSteps';
import VideoConferenceAdvantages from '../components/VideoConferenceAdvantages';
import { useTenantConfig } from '../hooks/useTenant';

const P = {
    main: '#03abdfff',
    light: '#72adf4',
    dark: '#276bdb',
};

const VideoKonferansPage = () => {
    const {tenantName} = useTenantConfig();
    const navigate = useNavigate();

    const mainFeatures = [
        {
            image: '/assets/icon/budget.webp',
            title: 'Sınırsız Toplantı Oluşturma',
            desc: 'Yönetim panelinden dilediğiniz zaman yeni video konferanslar oluşturun ve toplantılarınızı kolayca planlayın.',
            color: '#ec4899'
        },
        {
            image: '/assets/icon/queue.webp',
            title: 'Katılımcı Yönetimi',
            desc: 'Toplantıya katılacak kullanıcıları belirleyin ve davet bağlantılarını kolayca gönderin.',
            color: '#8b5cf6'
        },
        {
            image: '/assets/icon/experience.webp',
            title: 'Ekran Paylaşımı',
            desc: 'Sunumlarınızı paylaşın, belgeleri gösterin ve toplantı sırasında ekranınızı katılımcılarla paylaşın.',
            color: '#0891b2'
        },
        {
            image: '/assets/icon/user.webp',
            title: 'Anlık Mesajlaşma',
            desc: 'Toplantı sırasında katılımcılar chat özelliği ile iletişim kurabilir.',
            color: '#f97316'
        },
        {
            image: '/assets/icon/analysis.webp',
            title: 'Anket ve Oylama',
            desc: 'Toplantı sırasında anket oluşturabilir ve katılımcı görüşlerini hızlıca toplayabilirsiniz.',
            color: '#10b981'
        },
        {
            image: '/assets/icon/efficiency.webp',
            title: 'Ses Yönetimi',
            desc: 'Moderasyon paneli ile katılımcı mikrofonlarını kontrol edebilir ve toplantı düzenini sağlayabilirsiniz.',
            color: '#eab308'
        }
    ];

    const trustFeatures = [
        { icon: <Security />, text: 'Parola korumalı toplantılar' },
        { icon: <Speed />, text: 'HD kalitede görüntü' },
        { icon: <Assessment />, text: 'Detaylı katılım raporları' },
        { icon: <CheckCircle />, text: 'Tüm cihazlarla uyumlu' }
    ];

    return (
        <Box>
            {/* 1️⃣ HERO BÖLÜMÜ */}
            <Box
                sx={{
                    pt: { xs: 6, md: 10 },
                    pb: { xs: 6, md: 10 },
                    background: `linear-gradient(135deg, ${alpha(P.main, 0.06)} 0%, #ffffff 100%)`,
                    position: 'relative',
                    overflow: 'hidden',
                    borderBottom: '1px solid #e5e7eb',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '-20%',
                        right: '-10%',
                        width: 600,
                        height: 600,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${alpha(P.main, 0.12)} 0%, transparent 70%)`,
                        filter: 'blur(80px)',
                        pointerEvents: 'none',
                    }}
                />

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box maxWidth="720px">
                        <Chip
                            label="Video Konferans Modülü"
                            sx={{
                                mb: 3,
                                fontWeight: 700,
                                fontSize: '0.8rem',
                                background: alpha(P.main, 0.12),
                                color: P.dark,
                                border: `1px solid ${alpha(P.main, 0.2)}`,
                            }}
                        />

                        <Typography
                            variant="h1"
                            sx={{
                                fontWeight: 900,
                                fontSize: { xs: '2.5rem', md: '3.5rem' },
                                mb: 3,
                                lineHeight: 1.1,
                                color: '#0f172a',
                                letterSpacing: '-0.02em',
                            }}
                        >
                            Dernek Toplantılarınızı{' '}
                            <Box
                                component="span"
                                sx={{
                                    background: `linear-gradient(135deg, ${P.main} 0%, ${P.light} 100%)`,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                }}
                            >
                                Dijital Ortamda Gerçekleştirin
                            </Box>
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: { xs: '1.1rem', md: '1.25rem' },
                                color: '#64748b',
                                lineHeight: 1.7,
                                mb: 5,
                            }}
                        >
                            {tenantName} Video Konferans modülü sayesinde dernek toplantılarınızı doğrudan yönetim
                            paneli üzerinden gerçekleştirebilirsiniz. Zoom veya benzeri üçüncü parti uygulamalara
                            ihtiyaç duymadan güvenli ve pratik bir şekilde çevrim içi toplantılar düzenleyin.
                        </Typography>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Button
                                variant="contained"
                                size="large"
                                endIcon={<ArrowForward />}
                                onClick={() => navigate('/basvuru')}
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    background: `linear-gradient(135deg, ${P.main} 0%, ${P.light} 100%)`,
                                    boxShadow: `0 8px 24px ${alpha(P.main, 0.3)}`,
                                    '&:hover': {
                                        boxShadow: `0 12px 32px ${alpha(P.main, 0.4)}`,
                                        transform: 'translateY(-2px)',
                                    },
                                }}
                            >
                                Hemen Başlayın
                            </Button>
                        </Stack>
                    </Box>
                </Container>
            </Box>

            {/* 2️⃣ ANA ÖZELLİKLER */}
            <Box sx={{ py: { xs: 8, md: 12 } ,  bgcolor: '#ffffff' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 8 }}>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 800,
                                mb: 3,
                                color: '#0f172a',
                                fontSize: { xs: '1.9rem', md: '2.4rem' }
                            }}
                        >
                            Video Konferans Özellikleri
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '1.05rem',
                                color: '#475569',
                                lineHeight: 1.8,
                                mb: 4,
                            
                            }}
                        >
                            Tek platformdan tüm toplantı süreçlerini yönetin
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        {mainFeatures.map((item, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <Box
                                    sx={{
                                        p: 4,
                                        height: '100%',
                                        borderRadius: 4,
                                        border: '1px solid #e5e7eb',
                                        background: 'white',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            boxShadow: `0 8px 32px ${alpha(item.color, 0.12)}`,
                                            borderColor: alpha(item.color, 0.3),
                                            transform: 'translateY(-4px)',
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            borderRadius: 3,
                                            background: `linear-gradient(135deg, ${alpha(item.color, 0.1)} 0%, ${alpha(
                                                item.color,
                                                0.05
                                            )} 100%)`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 3,
                                        }}
                                    >
                                        <img src={item.image} alt={item.title} style={{ width: 36, height: 36 }} />
                                    </Box>

                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: '1.1rem',
                                            mb: 1.5,
                                            color: '#1e293b',
                                        }}
                                    >
                                        {item.title}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: '#64748b',
                                            lineHeight: 1.7,
                                            fontSize: '0.95rem',
                                        }}
                                    >
                                        {item.desc}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            <VideoMeetingSteps />
            <VideoConferenceAdvantages />

            {/* 3️⃣ GÜVEN & GÜVENLİK */}
            <Box sx={{ py: { xs: 8, md: 10 }, background: '#f8fafc' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 5 }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: '1.5rem', md: '1.75rem' },
                                mb: 1,
                                color: '#0f172a',
                            }}
                        >
                            Güvenli Altyapı
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '0.95rem',
                                color: '#64748b',
                            }}
                        >
                            Kurumsal güvenlik standartları
                        </Typography>
                    </Box>

                    <Grid container spacing={3} justifyContent="center">
                        {trustFeatures.map((f, i) => (
                            <Grid item xs={12} sm={6} md={3} key={i}>
                                <Box
                                    sx={{
                                        p: 2.5,
                                        textAlign: 'center',
                                        borderRadius: 3,
                                        border: '1px solid #e5e7eb',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 2,
                                            background: '#f1f5f9',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto',
                                            mb: 2,
                                            color: '#64748b',
                                            '& svg': { fontSize: 26 },
                                        }}
                                    >
                                        {f.icon}
                                    </Box>
                                    <Typography 
                                        sx={{ 
                                            fontWeight: 500, 
                                            color: '#64748b', 
                                            fontSize: '0.9rem',
                                        }}
                                    >
                                        {f.text}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* 4️⃣ CTA */}
            <Box
                sx={{
                    py: { xs: 8, md: 12 },
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            background: `linear-gradient(135deg, ${P.main} 0%, ${P.light} 50%, ${P.dark} 100%)`,
                            color: 'white',
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: 4,
                            py: { xs: 6, md: 8 },
                            px: { xs: 3, md: 6 },
                        }}
                    >
                        {/* Arka plan deseni */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                opacity: 0.15,
                                backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                                backgroundSize: '30px 30px'
                            }}
                        />

                        {/* Decorative circles */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '-10%',
                                right: '-5%',
                                width: 400,
                                height: 400,
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.1)',
                                filter: 'blur(60px)',
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: '-10%',
                                left: '-5%',
                                width: 300,
                                height: 300,
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.08)',
                                filter: 'blur(50px)',
                            }}
                        />

                        <Box sx={{ position: 'relative', textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
                            <Typography
                                variant="h2"
                                sx={{
                                    fontSize: { xs: '1.75rem', md: '2.5rem' },
                                    fontWeight: 900,
                                    mb: 3,
                                    textShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                }}
                            >
                                Dernek Toplantılarınızı Dijital Ortama Taşıyın
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: { xs: '1.05rem', md: '1.2rem' },
                                    mb: 5,
                                    opacity: 0.95,
                                    maxWidth: '580px',
                                    mx: 'auto',
                                    lineHeight: 1.7,
                                    color: 'rgba(255,255,255,0.9)'
                                }}
                            >
                                {tenantName} Video Konferans modülü ile toplantılarınızı güvenli, pratik ve tek platform
                                üzerinden yönetin.
                            </Typography>

                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={3}
                                justifyContent="center"
                            >
                                <Button
                                    variant="contained"
                                    size="large"
                                    //onClick={() => navigate('/basvuru')}
                                    sx={{
                                        bgcolor: 'white',
                                        color: P.main,
                                        px: 5,
                                        py: 2,
                                        fontSize: '1.05rem',
                                        fontWeight: 700,
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                        '&:hover': {
                                            bgcolor: '#fafafa',
                                            transform: 'translateY(-3px)',
                                            boxShadow: '0 8px 30px rgba(0,0,0,0.2)'
                                        }
                                    }}
                                >
                                    Hemen Başvurun
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    onClick={() => navigate('/iletisim')}
                                    sx={{
                                        borderColor: 'white',
                                        color: 'white',
                                        px: 5,
                                        py: 2,
                                        fontSize: '1.05rem',
                                        fontWeight: 700,
                                        borderWidth: 2,
                                        backdropFilter: 'blur(10px)',
                                        background: 'rgba(255,255,255,0.1)',
                                        '&:hover': {
                                            borderColor: 'white',
                                            borderWidth: 2,
                                            bgcolor: 'rgba(255,255,255,0.2)',
                                            transform: 'translateY(-3px)',
                                            boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                                        }
                                    }}
                                >
                                    İletişime Geçin
                                </Button>
                            </Stack>

                        </Box>
                    </Box>
                </Container>
            </Box>

        </Box>
    );
};

export default VideoKonferansPage;